import { useState, useEffect, useRef } from 'react'

const cache = new Map<string, string>()

function getToken(): string | null {
  return (window as Window & { __fasico_token?: string }).__fasico_token
    || sessionStorage.getItem('fasico_token')
}

export function usePlanPreview(projectId: string, enabled: boolean, refreshKey?: string) {
  const cacheKey = refreshKey ? `${projectId}:${refreshKey}` : projectId
  const prevCacheKey = useRef<string | null>(null)

  // Si la clave cambió (proyecto actualizado), limpiar entradas obsoletas del proyecto
  if (prevCacheKey.current !== null && prevCacheKey.current !== cacheKey) {
    for (const key of cache.keys()) {
      if (key === prevCacheKey.current || key.startsWith(`${projectId}:`)) {
        cache.delete(key)
      }
    }
  }
  prevCacheKey.current = cacheKey

  const [url, setUrl] = useState<string | null>(() => cache.get(cacheKey) ?? null)
  const [loading, setLoading] = useState(!cache.has(cacheKey) && enabled)

  useEffect(() => {
    if (!enabled || cache.has(cacheKey)) return

    let objectUrl: string | null = null
    let cancelled = false
    const token = getToken()
    if (!token) {
      setLoading(false)
      return
    }

    const apiBase = (import.meta.env.VITE_API_URL as string | undefined) ?? 'http://localhost:8000'
    // Incluir refreshKey como query param para invalidar caché del browser (Cache-Control)
    const qs = refreshKey ? `?t=${encodeURIComponent(refreshKey)}` : ''

    fetch(`${apiBase}/api/v1/projects/${projectId}/plan-preview${qs}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(res => {
        if (!res.ok) throw new Error(`${res.status}`)
        return res.blob()
      })
      .then(blob => {
        if (cancelled) return
        objectUrl = URL.createObjectURL(blob)
        cache.set(cacheKey, objectUrl)
        setUrl(objectUrl)
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  // refreshKey ya está capturado en cacheKey (= `${projectId}:${refreshKey}`):
  // cuando refreshKey cambia, cacheKey cambia y el efecto se re-ejecuta.
  // Agregar refreshKey directamente sería redundante.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, enabled, cacheKey])

  return { url, loading }
}
